import React, { useEffect } from 'react';
import './TermsOfServices.css';
import NavBar from '../../components/NavBar/NavBar';
import Footer from '../../components/Footer/Footer';
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import FloatingWebsiteNavigation from '../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation';


function TermsOfServices() {
  const { translations: tRaw, language } = useLanguage();
  const t = { terms: {}, ...tRaw, terms: { ...tRaw?.terms } }; // Safe fallback

  const isRTL = language === 'ar' || language === 'fa' || language === 'ur';

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0.1
    });

    document.querySelectorAll('.scroll-animate').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
    <div className="nav-desktop-only">
      <NavBar/>
    </div>
    <div className="terms-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="terms-header scroll-animate">
         <h1>{t.terms.title || "Terms of Service"}</h1>
        <p className="last-updated">{t.terms.lastUpdated || "Last updated"}: {new Date().toLocaleDateString()}</p>
      </header>

      <div className="terms-content">
        <section className="terms-section scroll-animate">
          <h2>{t.terms.introductionTitle || "1. Introduction"}</h2>
          <article className="terms-article">
            <p>{t.terms.introductionP1 || "The iWinto application is a platform designed to provide innovative social networking tools, simplifying the process of showcasing and selling goods and services while balancing social benefits with financial profitability. The application's role is to support individuals and businesses by offering flexible and secure payment solutions while ensuring the protection of user rights and compliance with local and global regulations."}</p>
            <p>{t.terms.introductionP2 || "The application serves as a technological interface catering to a wide range of users, from individuals to businesses, ensuring a safe and inclusive digital environment that fosters economic and social growth."}</p>
            <p>{t.terms.introductionP3 || "We are committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy is an integral part of our Terms of Use, detailing how we collect, use, and safeguard the information you provide when using our website or application. By using our services, you fully agree to this policy."}</p>
          </article>
        </section>

        <section className="terms-section scroll-animate">
          <h2>{t.terms.generalDefinitionsTitle || "2. General Definitions"}</h2>
          
          <article className="terms-article">
            <h3>{t.terms.applicationDefinitionsTitle || "2.1. Application Definitions"}</h3>
            <p>{t.terms.applicationDefinitions || "iWinto is a digital platform supporting e-commerce, retail and wholesale sales, and the provision of various services. The application allows users to accumulate financial balances using an integrated profit-based points system (single-tier referral system based on Affiliate Marketing). Additionally, the app fosters community engagement through innovative communication tools that facilitate interaction, idea-sharing, and experience exchange."}</p>
          </article>

          <article className="terms-article">
            <h3>{t.terms.applicationPrinciplesTitle || "2.2. Application Principles"}</h3>
            <ul>
              <li>{t.terms.principle1 || "The application does not support any form of network marketing or pyramid schemes."}</li>
              <li>{t.terms.principle2 || "The application strictly combats money laundering and adheres to all local and international financial crime regulations."}</li>
              <li>{t.terms.principle3 || "The application is committed to preventing the spread of false, misleading, or offensive content and does not tolerate any violations that may harm users or the community."}</li>
              <li>{t.terms.principle4 || "The application promotes a safe and respectful digital environment that upholds general ethics and publishing laws."}</li>
            </ul>
          </article>

          <article className="terms-article">
            <h3>{t.terms.objectivesTitle || "2.3. Objectives"}</h3>
            <ul>
              <li>{t.terms.objective1 || "Enhancing social interaction among users in creative and innovative ways."}</li>
              <li>{t.terms.objective2 || "Providing innovative economic solutions for businesses and consumers to improve service quality."}</li>
              <li>{t.terms.objective3 || "Facilitating financial transactions through secure and efficient payment technologies."}</li>
              <li>{t.terms.objective4 || "Fighting illegal activities such as pyramid schemes and money laundering, ensuring a clean digital environment."}</li>
              <li>{t.terms.objective5 || "Supporting local and global economies by stimulating innovation and increasing productivity."}</li>
            </ul>
          </article>

           <article className="terms-article">
        <h3>{t.terms.terminologyTitle || "2.4. Terminology Definitions"}</h3>
        <ul>
          <li><strong>{t.terms.termUser || "User"}</strong>: {t.terms.termUserDesc || "Any individual utilizing the iWinto application for personal, social, or commercial purposes."}</li>
          <li><strong>{t.terms.termData || "Personal Data"}</strong>: {t.terms.termDataDesc || "Any information related to an identifiable user including contact information, preferences, and usage data."}</li>
          <li><strong>{t.terms.termPlatform || "Website/Application"}</strong>: {t.terms.termPlatformDesc || "Refers to the iWinto digital platform available via web or mobile devices."}</li>
          <li><strong>{t.terms.termPolicy || "Policy"}</strong>: {t.terms.termPolicyDesc || "Refers to the current Privacy Policy and Terms of Use applicable to users."}</li>
        </ul>
      </article>
        </section>

      <section className="terms-section scroll-animate">
      <h2>{t.terms.networkTitle || "3. Combating Network Marketing"}</h2>

      <article className="terms-article">
        <h3>{t.terms.unethicalTitle || "3.1. Prohibition of Unethical Practices"}</h3>
        <ul>
          <li>{t.terms.unethical1 || "The application strictly prohibits any activity that involves recruiting new participants for financial gain instead of selling actual products or services."}</li>
          <li>{t.terms.unethical2 || "Financial activities within the platform are monitored to ensure legality."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.pyramidTitle || "3.2. Ban on Promoting Pyramid Business Models"}</h3>
        <ul>
          <li>{t.terms.pyramid1 || "The application's Terms of Use prohibit any attempts to promote network marketing schemes."}</li>
          <li>{t.terms.pyramid2 || "Accounts involved in such activities will be immediately suspended or removed."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.educationTitle || "3.3. Awareness and Education"}</h3>
        <ul>
          <li>{t.terms.education1 || "The application contributes to raising awareness about the risks of network marketing."}</li>
          <li>{t.terms.education2 || "Clear guidelines are provided to differentiate legitimate commercial activities from illegal ones."}</li>
        </ul>
      </article>
    </section>

    <section className="terms-section scroll-animate">
      <h2>{t.terms.referralTitle || "4. Legal Referral Process in iWinto"}</h2>

      <article className="terms-article">
        <h3>{t.terms.referralDefTitle || "4.1. Definition of Referral"}</h3>
        <p>{t.terms.referralDef || "iWinto allows users to refer new participants through an individual referral system (Referral Program), a globally recognized legal digital marketing mechanism. This system does not fall under network or pyramid marketing but instead follows the Affiliate Marketing model."}</p>
      </article>

      <article className="terms-article">
        <h3>{t.terms.referralMechTitle || "4.2. Referral Mechanism"}</h3>
        <ul>
          <li><strong>{t.terms.referralCode || "Referral Code Distribution"}</strong>: {t.terms.referralCodeDesc || "Each user receives a unique referral code that can be shared with others."}</li>
          <li><strong>{t.terms.referralUsage || "Using the Referral Code"}</strong>: {t.terms.referralUsageDesc || "When a new user registers with a referral code, their connection to the referrer is documented."}</li>
          <li><strong>{t.terms.referralReward || "Rewards"}</strong>: {t.terms.referralRewardDesc || "The referrer receives a financial reward, points, or in-app benefits, provided the referred user is active and meets the minimum engagement criteria set by the application."}</li>
          <li><strong>{t.terms.referralNoMulti || "No Multi-Level Commissions"}</strong>: {t.terms.referralNoMultiDesc || "Rewards are granted only once when a new user registers. There are no additional commissions for subsequent referrals by the referred user, distinguishing this system from prohibited pyramid marketing."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.referralComplianceTitle || "4.3. Compliance with Laws & Prevention of Pyramid Marketing"}</h3>
        <ul>
          <li>{t.terms.referralC1 || "The referral system is not based on recruiting members solely for profit but on actual service engagement, making it a legal affiliate model."}</li>
          <li>{t.terms.referralC2 || "Rewards are not given for hierarchical or chain-based recruitment; they are exclusively based on direct and real referrals."}</li>
          <li>{t.terms.referralC3 || "iWinto reserves the right to review any account suspected of misusing or manipulating the referral system and to take appropriate action per the platform's policies."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.referralTransTitle || "4.4. Commitment to Transparency Standards"}</h3>
        <ul>
          <li>{t.terms.referralT1 || "The referral program is part of a globally approved growth strategy, aligning with e-commerce and consumer protection laws in all operational regions."}</li>
          <li>{t.terms.referralT2 || "No user is allowed to market the referral program as an investment system or a guaranteed income opportunity; it is solely a promotional feature that rewards users for recommending the application."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.referralAgreeTitle || "4.5. Acceptance & Agreement"}</h3>
        <p>{t.terms.referralAgreement || "All users participating in the referral system agree to its terms and conditions and acknowledge that misleading or illegal promotion may result in account suspension or legal action per iWinto's policies."}</p>
      </article>
    </section>

    <section className="terms-section scroll-animate">
      <h2>{t.terms.amlTitle || "5. Anti-Money Laundering Policy"}</h2>

      <article className="terms-article">
        <h3>{t.terms.kycTitle || "5.1. Identity Verification (KYC)"}</h3>
        <ul>
          <li>{t.terms.kyc1 || "Users are required to provide identity verification documents upon registration or when conducting significant transactions."}</li>
          <li>{t.terms.kyc2 || "These documents include national ID, passport, or any other official identification document."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.monitoringTitle || "5.2. Monitoring Suspicious Activities"}</h3>
        <ul>
          <li>{t.terms.monitoring1 || "The application employs advanced systems to detect unusual or suspicious financial activities."}</li>
          <li>{t.terms.monitoring2 || "This includes large transactions, frequent transfers in a short period, or irregular account usage."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.reportingTitle || "5.3. Legal Reporting"}</h3>
        <ul>
          <li>{t.terms.reporting1 || "Any suspicious activity is reported to the relevant legal authorities in compliance with local and international laws."}</li>
          <li>{t.terms.reporting2 || "The application fully cooperates with authorities to provide necessary evidence."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.riskTitle || "5.4. Risk Mitigation"}</h3>
        <ul>
          <li>{t.terms.risk1 || "Strict procedures are applied to ensure that all funds flowing through the platform come from legitimate sources."}</li>
          <li>{t.terms.risk2 || "Any transactions violating anti-money laundering standards are rejected."}</li>
        </ul>
      </article>
    </section>

    <section className="terms-section scroll-animate">
      <h2>{t.terms.usageTitle || "6. Application Usage Terms"}</h2>

      <article className="terms-article">
        <h3>{t.terms.eligibilityTitle || "6.1. Eligibility"}</h3>
        <ul>
          <li>{t.terms.eligibility1 || "Users must be of legal age as per their country's laws. If a user is a minor, parental or guardian consent is required."}</li>
          <li>{t.terms.eligibility2 || "Users must provide accurate and truthful information during registration."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.responsibilitiesTitle || "6.2. Responsibilities"}</h3>
        <ul>
          <li>{t.terms.responsibility1 || "Users are responsible for all activities conducted through their accounts, including unauthorized use."}</li>
          <li>{t.terms.responsibility2 || "Users must not use the application for illegal or misleading purposes."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.appRightsTitle || "6.3. Application Rights"}</h3>
        <ul>
          <li>{t.terms.right1 || "Right to suspend or terminate accounts violating platform policies without prior notice."}</li>
          <li>{t.terms.right2 || "Right to modify terms of use as needed based on market demands or legal changes."}</li>
          <li>{t.terms.right3 || "Right to introduce new services or upgrade existing ones to enhance the user experience."}</li>
        </ul>
      </article>
    </section>

    <section className="terms-section scroll-animate">
      <h2>{t.terms.businessTitle || "7. Business Services"}</h2>

      <article className="terms-article">
        <h3>{t.terms.businessFeaturesTitle || "7.1. Business Features"}</h3>
        <ul>
          <li>{t.terms.business1 || "Enabling businesses to showcase their products/services to a broad audience."}</li>
          <li>{t.terms.business2 || "Providing advanced data analytics tools to enhance marketing strategies."}</li>
          <li>{t.terms.business3 || "Offering flexible payment solutions tailored for small, medium, and large businesses."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.businessSupportTitle || "7.2. Business Support"}</h3>
        <ul>
          <li>{t.terms.support1 || "Helping businesses expand locally and globally by accessing new markets."}</li>
          <li>{t.terms.support2 || "Providing financial and marketing consultations based on in-depth market analysis."}</li>
          <li>{t.terms.support3 || "Offering regular performance reports to support strategic decision-making."}</li>
        </ul>
      </article>
    </section>

    <section className="terms-section scroll-animate">
      <h2>{t.terms.govTitle || "8. Government Collaboration"}</h2>

      <article className="terms-article">
        <h3>{t.terms.govPartnerTitle || "8.1. Partnership with Governments"}</h3>
        <ul>
          <li>{t.terms.gov1 || "Supporting government initiatives in digital economy enhancement."}</li>
          <li>{t.terms.gov2 || "Providing data & analytics for policymaking in trade & industry."}</li>
        </ul>
      </article>

      <article className="terms-article">
        <h3>{t.terms.govComplianceTitle || "8.2. Compliance with Government Regulations"}</h3>
        <ul>
          <li>{t.terms.gov3 || "Ensuring all application activities align with local regulations."}</li>
          <li>{t.terms.gov4 || "Submitting regular reports to authorities to ensure transparency."}</li>
        </ul>
      </article>
    </section>
      </div>
    </div>
    <Footer/>
    <FloatingWebsiteNavigation />
    </>
  );
}

export default TermsOfServices;